import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { useDeviceLive } from '../api/deviceLive'
import { performanceDomainQuery, performanceManagementKick } from '../api/devices'
import type { PortalEvent } from '../api/portalSocket'
import { convertBytes } from './convertBytes'

/**
 * Filele live pe socket din Performance Management - statii Windows:
 * `Opteemum::liveConnections` / `liveCommunications` / `liveDiskspace` /
 * `liveDomain` (machetele `opteemum/devices/details/live*.php`).
 *
 * Acelasi drum ca in portal: browserul cere pe socket, serverul de socket duce
 * cererea la agent, agentul raspunde cu ce vede in clipa aia; nimic nu trece
 * prin baza. Fiecare fila cere la 10 secunde cat sta deschisa si rearmeaza
 * ghiontul SPD (`Opteemum::performanceKick`) la patru minute, sa nu adoarma
 * agentul in mijlocul privirii. Aceleasi tabele, aceleasi texte.
 */

const EVERY_MS = 10000
const KICK_EVERY_MS = 240000

/** Ghiontul: o data la deschidere (il da si capul din spate), apoi la patru minute. */
function useKick(id: number) {
  useEffect(() => {
    void performanceManagementKick(id)
    const timer = window.setInterval(() => void performanceManagementKick(id), KICK_EVERY_MS)
    return () => window.clearInterval(timer)
  }, [id])
}

function Note({ children }: { children: string }) {
  return (
    <tr>
      <td colSpan={4}>{children}</td>
    </tr>
  )
}

function Waiting({ status, at }: { status: string; at: number }) {
  if (status === 'unconfigured' || status === 'error') return <Note>Live socket is not available on this page.</Note>
  if (at === 0) return <Note>Waiting for the station to answer...</Note>
  return null
}

function useLive(id: number, event: PortalEvent, extra?: Record<string, string>) {
  const events = useMemo(() => [event], [event])
  return useDeviceLive(id, events, EVERY_MS, extra)
}

function asList(payload: unknown): Record<string, unknown>[] {
  return Array.isArray(payload) ? (payload as Record<string, unknown>[]) : []
}

function num(v: unknown): number {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function text(v: unknown): string {
  return v === null || v === undefined ? '' : String(v)
}

export function LiveConnections({ id }: { id: number }) {
  useKick(id)
  const live = useLive(id, 'connections')
  const list = asList(live.data.connections).sort((a, b) => num(b.count) - num(a.count))
  return (
    <div className="row">
      <div className="col-md-12">
        <p className="font-11" style={{ color: '#8a94a0' }}>
          <i className="fas fa-broadcast-tower"></i> Established connections, straight from the station. Refreshes every 10 seconds.
        </p>
        <table className="table table-striped table-hover" width="100%" id={`pm-live-connections-${id}`}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Process</th>
              <th style={{ textAlign: 'left' }}>Protocol</th>
              <th style={{ textAlign: 'left' }}>Foreign Address</th>
              <th style={{ textAlign: 'left' }}>Connections</th>
            </tr>
          </thead>
          <tbody>
            {live.at === 0 || live.status !== 'ready' ? (
              <Waiting status={live.status} at={live.at} />
            ) : list.length === 0 ? (
              <Note>No established connections reported.</Note>
            ) : (
              list.map((con, i) => (
                <tr key={i}>
                  <td>
                    <i className="far fa-window-maximize"></i> {text(con.process)}
                  </td>
                  <td>{text(con.protocol)}</td>
                  <td>{text(con.foreignAddress)}</td>
                  <td>{text(con.count)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export function LiveCommunications({ id }: { id: number }) {
  useKick(id)
  const live = useLive(id, 'communications')
  const list = asList(live.data.communications).sort((a, b) => num(b.read) + num(b.write) - (num(a.read) + num(a.write)))
  return (
    <div className="row">
      <div className="col-md-12">
        <p className="font-11" style={{ color: '#8a94a0' }}>
          <i className="far fa-comments"></i> Processes with network I/O, straight from the station. Refreshes every 10 seconds.
        </p>
        <table className="table table-striped table-hover" width="100%" id={`pm-live-communications-${id}`}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Process</th>
              <th style={{ textAlign: 'left' }}>Read</th>
              <th style={{ textAlign: 'left' }}>Write</th>
            </tr>
          </thead>
          <tbody>
            {live.at === 0 || live.status !== 'ready' ? (
              <Waiting status={live.status} at={live.at} />
            ) : list.length === 0 ? (
              <Note>No network I/O reported.</Note>
            ) : (
              list.map((app, i) => (
                <tr key={i}>
                  <td>
                    <i className="far fa-window-maximize"></i> {text(app.process)}
                  </td>
                  <td>{convertBytes(num(app.read))}</td>
                  <td>{convertBytes(num(app.write))}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const NAME_KEYS = ['name', 'Name', 'drive', 'Drive', 'letter', 'Letter', 'caption', 'Caption', 'mount', 'device']
const TOTAL_KEYS = ['total', 'Total', 'totalSpace', 'TotalSpace', 'total_space', 'size', 'Size']
const FREE_KEYS = ['free', 'Free', 'freeSpace', 'FreeSpace', 'free_space', 'available', 'Available']

function pick(disk: Record<string, unknown>, keys: string[]): unknown {
  for (const k of keys) if (disk[k] !== undefined) return disk[k]
  return undefined
}

/** Raspunsul agentului vine fie ca lista (`disks` / `drives`), fie ca obiect cu numele discului drept cheie. */
export function disksOf(payload: unknown): Record<string, unknown>[] {
  if (Array.isArray(payload)) return payload as Record<string, unknown>[]
  if (payload && typeof payload === 'object') {
    const p = payload as Record<string, unknown>
    if (Array.isArray(p.disks)) return p.disks as Record<string, unknown>[]
    if (Array.isArray(p.drives)) return p.drives as Record<string, unknown>[]
    const out: Record<string, unknown>[] = []
    for (const [name, disk] of Object.entries(p)) {
      if (disk && typeof disk === 'object') out.push({ name, ...(disk as Record<string, unknown>) })
    }
    return out
  }
  return []
}

export function LiveDiskspace({ id }: { id: number }) {
  useKick(id)
  const live = useLive(id, 'diskspace')
  const disks = disksOf(live.data.diskspace)
  const rows = disks
    .map((disk) => ({ name: pick(disk, NAME_KEYS), total: pick(disk, TOTAL_KEYS), free: pick(disk, FREE_KEYS) }))
    .filter((d) => d.name !== undefined || d.total !== undefined || d.free !== undefined)
  return (
    <div className="row">
      <div className="col-md-12">
        <p className="font-11" style={{ color: '#8a94a0' }}>
          <i className="far fa-hdd"></i> Free space on the fixed disks, straight from the station. Refreshes every 10 seconds.
        </p>
        <table className="table table-striped table-hover" width="100%" id={`pm-live-diskspace-${id}`}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Disk</th>
              <th style={{ textAlign: 'left' }}>Total</th>
              <th style={{ textAlign: 'left' }}>Free</th>
              <th style={{ textAlign: 'left' }}>Used</th>
            </tr>
          </thead>
          <tbody>
            {live.at === 0 || live.status !== 'ready' ? (
              <Waiting status={live.status} at={live.at} />
            ) : rows.length === 0 ? (
              <Note>No disk space reported.</Note>
            ) : (
              rows.map((d, i) => {
                const total = num(d.total)
                const free = num(d.free)
                const used = total > 0 && d.free !== undefined ? Math.max(0, Math.min(100, Math.round(((total - free) / total) * 100))) : null
                return (
                  <tr key={i}>
                    <td>
                      <i className="far fa-hdd"></i> {text(d.name)}
                    </td>
                    <td>{Number.isFinite(Number(d.total)) && d.total !== undefined ? convertBytes(total) : text(d.total)}</td>
                    <td>{Number.isFinite(Number(d.free)) && d.free !== undefined ? convertBytes(free) : text(d.free)}</td>
                    <td>
                      {used === null ? (
                        'n/a'
                      ) : (
                        <div className="progress" style={{ marginBottom: 0 }}>
                          <div
                            className={`progress-bar ${used >= 90 ? 'progress-bar-danger' : used >= 75 ? 'progress-bar-warning' : 'progress-bar-success'}`}
                            style={{ width: `${used}%`, minWidth: 36 }}
                          >
                            {used}%
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ServerRows({ title, servers }: { title: string; servers: unknown }) {
  if (!Array.isArray(servers)) return null
  const cell = { borderBottom: '1px solid #DDD', padding: '5px 0' }
  const grey = { borderBottom: '1px solid #DDD', padding: '5px 0 5px 5px', color: '#AAA' }
  return (
    <>
      <tr>
        <td className="font-11" style={cell}>
          {title}:
        </td>
        <td className="align-left" style={grey}></td>
      </tr>
      {(servers as Record<string, unknown>[]).map((server, i) => (
        <tr key={i}>
          <td className="font-11" style={cell}>
            <b>{text(server.name)}</b>
          </td>
          <td className="align-left" style={grey}>
            <p>
              <b>IP:</b> {server.ip ? text(server.ip) : 'n/a'}
            </p>
            <p>
              <b>Ping:</b> {server.ping ? text(server.ping) : 'n/a'}
            </p>
            <p>
              <b>Port:</b> {server.port ? text(server.port) : 'n/a'}
            </p>
          </td>
        </tr>
      ))}
    </>
  )
}

function DomainBox({ id, email }: { id: number; email: string }) {
  const extra = useMemo(() => ({ email }), [email])
  const live = useLive(id, 'domain', extra)
  const response = (live.data.domain ?? null) as Record<string, unknown> | null
  const cell = { borderBottom: '1px solid #DDD', padding: '5px 0' }
  const grey = { borderBottom: '1px solid #DDD', padding: '5px 0 5px 5px', color: '#AAA' }
  return (
    <div className="row">
      <div className="col-md-12">
        <p className="font-11" style={{ color: '#8a94a0' }}>
          <i className="fa fa-paper-plane"></i> Whois / MX / NS for the domain of <b>{email}</b>, resolved by the station itself.
        </p>
        <div
          id={`pm-live-domain-${id}`}
          style={{ marginBottom: 10, padding: '10px 20px', borderRadius: 4, border: '1px solid #ddd', borderLeft: '6px solid #DDD', borderRight: '6px solid #DDD' }}
        >
          {live.status === 'unconfigured' || live.status === 'error' ? (
            'Live socket is not available on this page.'
          ) : live.at === 0 ? (
            'Waiting for the station to answer...'
          ) : !response || typeof response !== 'object' ? (
            'The station did not answer the domain check.'
          ) : (
            <table style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <td className="font-11" style={cell}>
                    <b>Domain</b>
                  </td>
                  <td className="align-left" style={grey}>
                    <p>{text(response.domain)}</p>
                  </td>
                </tr>
                <tr>
                  <td className="font-11" style={cell}>
                    <b>Webport</b>
                  </td>
                  <td className="align-left" style={grey}>
                    <p>{text(response.webport)}</p>
                  </td>
                </tr>
                <ServerRows title="Name Servers" servers={response.nameservers} />
                <ServerRows title="Mail Servers" servers={response.mailservers} />
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

export function LiveDomain({ id }: { id: number }) {
  useKick(id)
  const info = useQuery(performanceDomainQuery(id))
  if (info.isPending) return <div className="loading">Loading...</div>
  if (info.isError) return <div className="alert alert-danger">{info.error.message}</div>
  if (!info.data.email) {
    return (
      <div className="alert alert-info" style={{ margin: 0 }}>
        <i className="fas fa-info-circle"></i> The owner of this device has no email address on file, so there is no domain to check.
      </div>
    )
  }
  return <DomainBox id={id} email={info.data.email} />
}
